"use client";

import * as React from "react";
import {
  ColumnDef,
} from "@tanstack/react-table";
import { MoreVerticalIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AppleIcon,
  SmartphoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  WifiIcon,
  ShieldCheckIcon,
  BadgeCheckIcon,
  LockIcon,
} from "lucide-react";
import AuthorizeDevice from "./components/AuthorizeDevice";
import UnauthorizeDevice from "./components/UnauthorizeDevice";
import CommonList from "@/components/common/List";
import CommonTableList from "@/components/common/TableList";
import axiosInstance from "@/utils/axiosInstance";
import { Vehicle } from "@/interface/entityInterface";
import CommonDelete from "@/components/common/Delete";
import AssignVehicle from "./components/AssignVehicle";

const CoreDevicesList = () => {
  const [vehiclesList, setVehicles] = React.useState<Vehicle[]>([]);

  React.useEffect(() => {
    const getVehicleList = async () => {
      const { data } = await axiosInstance.get("/vehicles", {
        params: {
          start: 1, end: 10000
        }
      });
      setVehicles(data);
    };

    getVehicleList();
  }, []);

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ row }) => <div>{row.getValue("id")}</div>,
    },
    {
      id: "user",
      header: "User",
      cell: ({ row }) => {
        const user = row.original.user;
        return (
          <div className="flex flex-col text-sm">
            <div className="flex items-center space-x-2">
              <UserIcon className="w-4 h-4 text-muted-foreground" />
              <span>
                {user?.firstname} {user?.lastname}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">{user?.email}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "device_brand",
      header: "Device Brand",
      cell: ({ row }) => {
        const brand = (row.getValue("device_brand") as string)?.toLowerCase();
        let icon;
        if (brand.includes("samsung") || brand.includes("android")) {
          icon = <SmartphoneIcon className="w-4 h-4 text-green-600" />;
        } else if (brand.includes("apple") || brand.includes("iphone")) {
          icon = <AppleIcon className="w-4 h-4 text-gray-800" />;
        } else {
          icon = <SmartphoneIcon className="w-4 h-4 text-muted-foreground" />;
        }

        return (
          <div className="flex items-center space-x-2">
            {icon}
            <span className="capitalize">{brand || "-"}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "app_version",
      header: "App Version",
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <BadgeCheckIcon className="w-4 h-4 text-blue-600" />
          <span>{row.getValue("app_version") || "-"}</span>
        </div>
      ),
    },
    {
      accessorKey: "device_ip",
      header: "IP Address",
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <WifiIcon className="w-4 h-4 text-muted-foreground" />
          <span>{row.getValue("device_ip") || "-"}</span>
        </div>
      ),
    },
    {
      accessorKey: "active",
      header: "Active",
      cell: ({ row }) => {
        const active = row.getValue("active");
        return (
          <div className="flex items-center space-x-2">
            {active ? (
              <CheckCircleIcon className="w-4 h-4 text-green-600" />
            ) : (
              <XCircleIcon className="w-4 h-4 text-red-500" />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "assign_vehicle",
      header: "Assign to vehicle",
      cell: ({ row }: any) => <AssignVehicle url="devices" id={row.original.id} vehicleId={row.original.vehicle_id} vehicleList={vehiclesList} />,
    },
    {
      accessorKey: "authorized",
      header: "Authorized",
      cell: ({ row }) => {
        const auth = row.getValue("authorized");
        return (
          <div className="flex items-center space-x-2">
            {auth ? (
              <ShieldCheckIcon className="w-4 h-4 text-green-600" />
            ) : (
              <LockIcon className="w-4 h-4 text-yellow-500" />
            )}
            <span>{auth ? "Yes" : "No"}</span>
          </div>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
              size="icon"
            >
              <MoreVerticalIcon />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <AuthorizeDevice
              id={row.original.id}
              title="Authorize Device"
              description="This action can not be undone"
            />
            <UnauthorizeDevice
              id={row.original.id}
              title="Unauthorize Device"
              description="This action can not be undone"
            />
            <DropdownMenuSeparator />
            <CommonDelete id={row.original.id} responseMessage="Device deleted" resource="devices" />
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div>
      <CommonList>
        <CommonTableList columns={columns} filterSearch="device_brand" />
      </CommonList>
    </div>
  );
};

export default CoreDevicesList;
